import type { DecisionGraph } from "@imminently/decisively-core";
import type { CompiledDerivedTable, DerivedTableResult } from "@packages/commons";

export const GLOBAL_ENTITY = "global";

/**
 * Every distinct instance path (eg. "children/1") of `entity` in the debugger's already-resolved
 * decision graph - the graph is fetched on every normal debugger run regardless of the derived
 * table view, so no request of its own. Read from the node instances (no paths or node objects
 * made for the other nodes).
 */
export const listEntityInstancePaths = (graph: DecisionGraph | null | undefined, entity: string): string[] => {
  if (!graph) return [];
  const seen = new Set<number>();
  const paths: string[] = [];
  for (const id of graph.ids()) {
    const instance = graph.instance(id);
    if (instance < 0 || seen.has(instance)) continue;
    seen.add(instance);
    if (graph.entity(id) === entity) paths.push(graph.instancePath(id)!);
  }
  return paths;
};

/**
 * The exact `table` string to request for a table + (optional) selected instance - also used as
 * the request's `id`, so a response can be matched back to the selection that produced it.
 * Returns null when the selection isn't complete enough to fetch yet (eg. an entity-scoped table
 * with no instance chosen) - the caller should not fetch in that case.
 */
export const computeDerivedTableRequestKey = (
  table: Pick<CompiledDerivedTable, "id" | "entity"> | undefined | null,
  selectedInstance: string | null,
): string | null => {
  if (!table) return null;
  if (table.entity === GLOBAL_ENTITY) return table.id;
  return selectedInstance ? `${selectedInstance}/${table.id}` : null;
};

/** The minimal shape of a debugger session record this module reads from. */
interface SessionRecordLike {
  // On the wire this is Rust's `ResultOutput`: absent, a bare object (one `derivedTable` element
  // requested), or an array (more than one requested in the same response). This module only ever
  // requests one at a time, so the array case is treated as "no match" below rather than searched.
  derivedTables?: DerivedTableResult | DerivedTableResult[];
}

/**
 * The fetched result for `requestKey`, or undefined if we don't have one yet (not fetched, still
 * pending, or `record.derivedTables` belongs to a different, now-stale selection).
 */
export const getMatchingDerivedTableResult = (
  record: SessionRecordLike | null | undefined,
  requestKey: string | null,
): DerivedTableResult | undefined => {
  if (!requestKey) return undefined;
  const result = record?.derivedTables;
  if (!result || Array.isArray(result)) return undefined;
  return result.id === requestKey ? result : undefined;
};

/** `null` on the wire means uncertain/unknown/empty - there's no way to distinguish those cases from JSON alone. */
export const formatCellValue = (value: string | number | boolean | string[] | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};
